import { cn } from "@/utils/helpers"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"
import * as React from "react"
import { Label } from "./Label"

const ListItemVariants = cva(
  "flex items-center pl-4 pr-3 py-3 bg-inherit gap-2 justify-between",
  {
    variants: {
      variant: {
        default: "",
        select: "",
        expand: "",
        toggle: "",
        choice: "",
      },

      size: {
        default: "h-14",
        lg: "h-17",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
)

export interface ListProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof ListItemVariants> {
  asChild?: boolean
}

const ListItemContent = React.forwardRef<
  HTMLDivElement,
  {
    icon?: JSX.Element
    title: string
    subText?: string
    subTitle?: string
    className?: string
    hasHandle?: boolean
  }
>(({ ...props }, ref) => {
  return (
    <div
      className={cn("pointer-events-none flex w-full items-center gap-4")}
      ref={ref}
    >
      {props.icon}
      <div className="w-full">
        <Label
          size="lg"
          className={cn(
            "text-on-surface w-3/4 truncate sm:w-4/5 lg:w-[90%] xl:w-[95%]",
            props.className,
          )}
        >
          {props.title}{" "}
          {props.subTitle && (
            <span className="text-on-surface-variant text-sm">
              ({props.subTitle})
            </span>
          )}
        </Label>
        {props.subText && (
          <Label
            size="md"
            className="text-on-surface-variant max-w-64 truncate"
          >
            {props.subText}
          </Label>
        )}
      </div>
    </div>
  )
})

ListItemContent.displayName = "ListItemContent"

const List = React.forwardRef<HTMLDivElement, ListProps>(
  ({ className, variant, size, asChild = false, children, ...props }, ref) => {
    const Comp = asChild ? Slot : "div"
    return (
      <Comp className={"flex flex-col"} ref={ref} {...props}>
        {children}
      </Comp>
    )
  },
)
List.displayName = "List"

export interface ListItemProps
  extends React.ButtonHTMLAttributes<HTMLDivElement>,
    VariantProps<typeof ListItemVariants> {
  icon?: JSX.Element
  subText?: string
  title?: string
  handle?: JSX.Element
  subTitle?: string
  contentClassName?: string
  handleClassName?: string
}

const ListItem = React.forwardRef<HTMLDivElement, ListItemProps>(
  (
    {
      className,
      contentClassName,
      variant,
      size,
      onClick,
      subText,
      icon,
      subTitle,
      title,
      handle,
      handleClassName,
      ...props
    },
    ref,
  ) => {
    return (
      <div
        className={cn(ListItemVariants({ variant, size }), className)}
        {...props}
        ref={ref}
      >
        <div onClick={onClick} className={cn("w-full", handle ? "w-4/5" : "")}>
          <ListItemContent
            icon={icon}
            title={title}
            subTitle={subTitle}
            subText={subText}
            className={contentClassName}
            hasHandle={!!handle}
          />
        </div>

        {handle && (
          <div className={cn("flex w-10 justify-end", handleClassName)}>
            {handle}
          </div>
        )}
      </div>
    )
  },
)
ListItem.displayName = "ListItem"

export { List, ListItem, ListItemVariants }
